import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapTestDetailDto, mapTestListItemDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mapMetricSorts } from '@/shared/data/metricSortFields';
import {
  buildEqFilter,
  buildSlugFilters,
  mergeFilters,
  type GridFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  TestDetailDto,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const resolveSuiteSortLabel = (item: TestListItemDto): string => {
  const primarySuiteName = item.suites?.items?.[0]?.name ?? null;
  if (primarySuiteName && primarySuiteName.trim().length > 0) {
    return primarySuiteName.toLowerCase();
  }
  const fallbackSuiteId = item.suites?.ids?.[0] ?? '';
  return fallbackSuiteId.toLowerCase();
};

const sortBySuiteName = (
  items: TestListItemDto[],
  direction: QuerySort['direction'],
): TestListItemDto[] => {
  const sorted = [...items];
  sorted.sort((left, right) => {
    const leftLabel = resolveSuiteSortLabel(left);
    const rightLabel = resolveSuiteSortLabel(right);
    const nameOrder = leftLabel.localeCompare(rightLabel);
    if (nameOrder !== 0) {
      return direction === 'asc' ? nameOrder : -nameOrder;
    }
    const leftName = left.name.toLowerCase();
    const rightName = right.name.toLowerCase();
    const testNameOrder = leftName.localeCompare(rightName);
    return direction === 'asc' ? testNameOrder : -testNameOrder;
  });
  return sorted;
};

export const queryTests = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsWindowDays?: number;
    metricsStartDay?: string;
    metricsEndDay?: string;
    previousMetricsStartDay?: string;
    previousMetricsEndDay?: string;
    include?: TestInclude;
  },
  tokenOverride?: string | null,
): Promise<QueryResult<TestListItemDto>> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters = !hasProjectFilter ? buildEqFilter('projectId', projectId ?? null) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const requestedSorts = input?.sorts ?? [{ field: 'createdAt', direction: 'desc' }];
  const suiteNameSort = requestedSorts.find((sort) => sort.field === 'suiteName') ?? null;
  const apiSortCandidates = requestedSorts.filter((sort) => sort.field !== 'suiteName');
  const apiSorts =
    apiSortCandidates.length > 0
      ? mapMetricSorts(apiSortCandidates)
      : [{ field: 'createdAt', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<TestListItemDto>>(
      'tests/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: apiSorts,
        filters: combinedFilters,
        metricsWindowDays: input?.metricsWindowDays,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        previousMetricsStartDay: input?.previousMetricsStartDay,
        previousMetricsEndDay: input?.previousMetricsEndDay,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    const mapped = mapQueryResult(result, mapTestListItemDto);
    if (!suiteNameSort) {
      return mapped;
    }
    return {
      ...mapped,
      items: sortBySuiteName(mapped.items, suiteNameSort.direction),
    };
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
  },
  tokenOverride?: string | null,
): Promise<TestSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters = !hasProjectFilter ? buildEqFilter('projectId', projectId ?? null) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestSummaryDto>(
      'tests/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
  },
  tokenOverride?: string | null,
): Promise<TestExecutionStatusSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters = !hasProjectFilter ? buildEqFilter('projectId', projectId ?? null) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestExecutionStatusSummaryDto>(
      'tests/summary-status',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestBySlug = async (
  slug: string | null,
  projectId?: string | null,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestDetailDto | null> => {
  if (!slug) {
    return null;
  }
  const token = ensureToken(tokenOverride);
  const filters = buildSlugFilters(slug);
  try {
    const payload = await apiClient.post<QueryResult<TestListItemDto>>(
      'tests/query',
      { page: 1, pageSize: 1, filters },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    const mapped = mapQueryResult(payload, mapTestListItemDto);
    if (mapped.items.length > 0) {
      return await getTestById(mapped.items[0].id, organisationId, tokenOverride);
    }
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw toApiError(err);
  }
};

export const getTestById = async (
  id: string,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<TestDetailDto>(`tests/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createTest = async (
  input: {
    name: string;
    projectId: string;
    featureIds?: string[];
    suiteIds?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    name: input.name.trim(),
    projectId: input.projectId.trim(),
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
  };
  try {
    const result = await apiClient.post<TestDetailDto>('tests', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateTest = async (
  input: {
    id: string;
    name?: string;
    featureIds?: string[];
    suiteIds?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    name: input.name?.trim(),
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
  };
  try {
    const result = await apiClient.patch<TestDetailDto>(`tests/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type TestInclude = {
  project?: boolean;
  features?: boolean;
  suites?: { ids?: boolean; items?: boolean; primary?: boolean };
  latestExecution?: boolean;
  metrics?: boolean;
  metricDeltas?: boolean;
};

export const deleteTest = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`tests/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
